import { createFileRoute, Link } from "@tanstack/react-router";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { Reveal } from "@/components/site/wow/Reveal";
import { ArrowRight } from "lucide-react";
import { aeoGeo } from "@/lib/seo/schema";

const CASES = [
  {
    client: "Garageportexperten",
    title: "Always-on video that doubled qualified leads",
    industry: "Home improvement",
    body: "A 12-month Pulse subscription powering Meta, YouTube and the company site. Result: 2.1× qualified leads at a 38% lower CPL.",
  },
  {
    client: "Norrhavet",
    title: "Brand film and recruitment films, same crew",
    industry: "Marketing group",
    body: "One brand anthem plus six recruitment shorts, shot back-to-back across two locations. Hiring funnel filled within six weeks.",
  },
  {
    client: "Ellary",
    title: "Product launch with multi-format delivery",
    industry: "Consumer brand",
    body: "Hero film for the press launch, plus 28 cutdowns for paid social and retail screens. End-to-end in three weeks.",
  },
  {
    client: "Nordic SaaS scale-up",
    title: "Customer story series for sales enablement",
    industry: "B2B SaaS",
    body: "Six customer films built into a structured sales asset library. Sales reps reported shorter cycles within the first quarter.",
  },
];

export const Route = createFileRoute("/en_/work")({
  head: () =>
    ({
      meta: [
        { title: "Our work — Selected video case studies | Fenny" },
        {
          name: "description",
          content:
            "Selected work from Fenny — brand films, product launches, recruitment video and always-on Pulse subscriptions for European brands.",
        },
        { property: "og:title", content: "Selected work — Fenny" },
        {
          property: "og:description",
          content:
            "Brand films, product launches and always-on video subscriptions that move the needle.",
        },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:url", content: "https://fenny.se/en/work" },
      ],
      links: [
        { rel: "canonical", href: "https://fenny.se/en/work" },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/work", name: "Our work — Selected video case studies | Fenny", description: "Selected work from Fenny — brand films, product launches, recruitment video and always-on Pulse subscriptions for European brands."})) },
      ],
    }) as never,
  component: WorkPage,
});

function WorkPage() {
  return (
    <div>
      <section className="relative overflow-hidden pt-32 pb-12 md:pt-44 md:pb-20">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_80%_10%,color-mix(in_oklab,var(--mint)_10%,transparent),transparent_70%)]"
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="mb-6 text-xs uppercase tracking-[0.22em] text-butter">
            Selected work
          </div>
          <KineticHeading
            text="Video that moves real business metrics."
            className="max-w-[22ch] text-balance text-5xl font-semibold leading-[0.98] text-foreground md:text-7xl"
            highlight={{ indices: [2], className: "text-mint italic font-serif font-normal" }}
          />
          <p className="mt-10 max-w-2xl text-pretty text-lg text-muted-foreground md:text-xl">
            A small slice of work for brands across Sweden, the Nordics and the
            wider EU. Every project is shipped by senior crew, on a fixed
            price, with measurable goals from day one.
          </p>
        </div>
      </section>

      <Reveal>
        <section className="py-16 md:py-24">
          <div className="container-wide">
            <div className="grid gap-8 md:grid-cols-2">
              {CASES.map((c) => (
                <article
                  key={c.client}
                  className="group rounded-3xl border border-white/10 bg-surface p-8 transition hover:border-white/20"
                >
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.22em] text-butter">
                    <span>{c.client}</span>
                    <span className="text-muted-foreground">{c.industry}</span>
                  </div>
                  <h3 className="mt-4 text-2xl font-semibold leading-tight">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {c.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="py-24 md:py-32 border-t border-white/10">
          <div className="container-wide text-center">
            <h2 className="mx-auto max-w-3xl text-balance text-4xl font-semibold leading-tight md:text-6xl">
              Want a tailored deck for your industry?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg text-muted-foreground">
              Tell us your sector and we'll send relevant case studies before
              your strategy call.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Magnetic strength={0.3}>
                <Link
                  to="/en/contact"
                  className="group inline-flex items-center gap-3 rounded-full bg-mint px-8 py-4 text-base font-semibold text-mint-foreground"
                >
                  Request case studies
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Magnetic>
              <Link
                to="/en/services"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-medium text-foreground/90 hover:bg-white/5"
              >
                Explore services
              </Link>
            </div>
          </div>
        </section>
      </Reveal>
    </div>
  );
}
